import { CertRiskBadge } from "@/components/CertRiskBadge";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, ChevronLeft, Users, CalendarCheck, Trash2, BookOpen, AlertTriangle, Download, Phone, Grid3x3, LayoutDashboard, PenLine, ChevronRight, ExternalLink, CalendarRange, CheckCircle2, FileText } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import GoogleCourseworkWidget from "@/components/GoogleCourseworkWidget";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import GermanDateInput from "@/components/GermanDateInput";
import WeiterbildungKundeDialog from "@/components/WeiterbildungKundeDialog";

const STATUS_LABELS = { present: "Anwesend", sick: "Krank", excused: "Entschuldigt", unexcused: "Unentschuldigt" };
const STATUS_STYLES = {
  present: "bg-emerald-50 border-emerald-400 text-emerald-800",
  sick: "bg-blue-50 border-blue-400 text-blue-800",
  excused: "bg-amber-50 border-amber-400 text-amber-800",
  unexcused: "bg-red-50 border-red-400 text-red-800",
};
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function WeiterbildungClassDetailPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const { classId } = useParams();
  const [cls, setCls] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [diary, setDiary] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("uebersicht");
  const [assignOpen, setAssignOpen] = useState(false);
  const [kundeDialogOpen, setKundeDialogOpen] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [attOpen, setAttOpen] = useState(false);
  const [diaryOpen, setDiaryOpen] = useState(false);
  const [interventionTarget, setInterventionTarget] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, p, a, d, w, ds] = await Promise.all([
        api.get(`/weiterbildung/classes/${classId}`),
        api.get(`/weiterbildung/participants`, { params: { class_id: classId } }),
        api.get(`/weiterbildung/classes/${classId}/attendance`),
        api.get(`/weiterbildung/classes/${classId}/diary`),
        api.get(`/weiterbildung/classes/${classId}/early-warnings`),
        api.get(`/weiterbildung/classes/${classId}/dashboard`),
      ]);
      setCls(c.data); setParticipants(p.data); setAttendance(a.data); setDiary(d.data); setWarnings(w.data); setDashboard(ds.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, [classId]); // eslint-disable-line react-hooks/exhaustive-deps

  const attendanceStats = useMemo(() => {
    const perParticipant = {};
    for (const a of attendance) {
      for (const e of (a.entries || [])) {
        if (!perParticipant[e.participant_id]) perParticipant[e.participant_id] = { total: 0, present: 0, sick: 0, excused: 0, unexcused: 0 };
        perParticipant[e.participant_id].total++;
        if (e.status) perParticipant[e.participant_id][e.status] = (perParticipant[e.participant_id][e.status] || 0) + 1;
      }
    }
    return perParticipant;
  }, [attendance]);

  const removeParticipant = async (p) => {
    if (!window.confirm(`Teilnehmer ${p.name || p.first_name} aus dieser Klasse entfernen? Die Kundendaten bleiben erhalten.`)) return;
    try {
      await api.post(`/weiterbildung/participants/${p.participant_id}/unassign`);
      toast.success("Aus Klasse entfernt"); fetchAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openContractPdf = async (p) => {
    try {
      const res = await api.get(`/weiterbildung/participants/${p.participant_id}/contract.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) {
      let detail = e.message;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail || detail; } catch { /* keep default */ }
      } else {
        detail = formatApiErrorDetail(e.response?.data?.detail) || detail;
      }
      toast.error(detail);
    }
  };

  if (loading) return <div className="p-8 text-xs text-slate-500">Lädt…</div>;
  if (!cls) return null;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="class-detail-page">
      <Link to="/weiterbildung" className="text-xs uppercase tracking-overline text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4">
        <ChevronLeft size={12} strokeWidth={1.5} /> Zurück
      </Link>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-1">{cls.measure_number} · {cls.weiterbildung_name}</div>
          <h1 className="font-heading text-5xl font-light tracking-tighter text-slate-900">{cls.name}</h1>
          <div className="text-xs text-slate-600 font-mono mt-2">
            {formatDeShort(cls.start_date)} – {formatDeShort(cls.end_date)}
            {cls.default_time_from && cls.default_time_to && ` · ${cls.default_time_from}–${cls.default_time_to} Uhr`}
          </div>
        </div>
        {cls.google_classroom_url && (
          <a
            href={cls.google_classroom_url}
            target="_blank" rel="noopener noreferrer"
            className="px-4 py-3 border border-[#1a73e8] text-[#1a73e8] hover:bg-[#1a73e8] hover:text-white flex items-center gap-2 text-sm font-medium transition-colors"
            data-testid="classroom-link-btn"
            title="Öffne Google Classroom für diese Klasse"
          >
            <ExternalLink size={14} strokeWidth={1.5} />
            Google Classroom
          </a>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-6">
        <TabBtn testId="tab-uebersicht" active={tab === "uebersicht"} onClick={() => setTab("uebersicht")} icon={LayoutDashboard}>Übersicht</TabBtn>
        <TabBtn testId="tab-monatsplan" active={tab === "monatsplan"} onClick={() => setTab("monatsplan")} icon={Grid3x3}>Monatsplan</TabBtn>
        <TabBtn testId="tab-wochenplan" active={tab === "wochenplan"} onClick={() => setTab("wochenplan")} icon={CalendarRange}>Wochenplan ({(cls?.weekly_plan || []).length})</TabBtn>
        <TabBtn testId="tab-teilnehmer" active={tab === "teilnehmer"} onClick={() => setTab("teilnehmer")} icon={Users}>Teilnehmer ({participants.length})</TabBtn>
        <TabBtn testId="tab-anwesenheit" active={tab === "anwesenheit"} onClick={() => setTab("anwesenheit")} icon={CalendarCheck}>Anwesenheit ({attendance.length})</TabBtn>
        <TabBtn testId="tab-klassenbuch" active={tab === "klassenbuch"} onClick={() => setTab("klassenbuch")} icon={BookOpen}>Klassenbuch ({diary.length})</TabBtn>
        <TabBtn testId="tab-fruehwarnung" active={tab === "fruehwarnung"} onClick={() => setTab("fruehwarnung")} icon={AlertTriangle}>Frühwarnung</TabBtn>
      </div>

      {tab === "uebersicht" && <ClassOverview dashboard={dashboard} onGotoTab={setTab} classId={classId} classData={cls} />}

      {tab === "monatsplan" && <MonthlyMatrix classId={classId} onChanged={fetchAll} defaultTimeFrom={cls?.default_time_from} defaultTimeTo={cls?.default_time_to} />}

      {tab === "wochenplan" && (
        <WeeklyPlanView
          weeks={cls?.weekly_plan || []}
          classStart={cls?.start_date}
          classEnd={cls?.end_date}
          classId={classId}
          canEdit={user && (user.role === "admin" || user.role === "manager" || (user.role === "lecturer" && (cls?.lecturer_ids || []).includes(user.user_id)))}
          onSaved={fetchAll}
        />
      )}

      {tab === "teilnehmer" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-light tracking-tighter">Teilnehmer</h2>
            {canManage && (
                <button onClick={() => setAssignOpen(true)} data-testid="add-participant-btn" className="text-xs bg-slate-900 text-white px-4 py-2 hover:bg-slate-800 flex items-center gap-2">
                <Plus size={12} strokeWidth={1.5} /> Teilnehmer zuweisen
              </button>
            )}
          </div>
          <div className="border border-slate-200 divide-y divide-slate-100">
            {participants.length === 0 && <div className="p-8 text-center text-slate-400 text-sm italic">Noch keine Teilnehmer in dieser Klasse. Kunden werden zentral unter Weiterbildung → „Kunden" erfasst und dann hier zugewiesen.</div>}
            {participants.map((p) => {
              const s = attendanceStats[p.participant_id];
              const total = s?.total || 0;
              const present = s?.present || 0;
              const quota = total > 0 ? Math.round((present / total) * 100) : null;
              const quotaColor = quota === null ? "text-slate-400" : quota >= 85 ? "text-emerald-600" : quota >= 80 ? "text-amber-600" : "text-red-600";
              return (
                <div key={p.participant_id} className="p-4 flex items-center gap-4" data-testid={`participant-row-${p.participant_id}`}>
                  <div className="flex-1 min-w-0">
                    <Link to={`/weiterbildung/kunden/${p.participant_id}`} className="text-sm font-medium text-slate-900 hover:underline underline-offset-4" data-testid={`class-participant-akte-${p.participant_id}`}>{p.name || `${p.first_name} ${p.last_name}`}</Link>
                    <div className="text-xs text-slate-500 font-mono">{p.email}</div>
                    <div className="mt-1"><CertRiskBadge present={present} total={total} testId={`cert-risk-${p.participant_id}`} /></div>
                    {(p.module_ids || []).length > 0 && (
                      <div className="text-[10px] text-slate-400 mt-1">{(p.module_ids || []).length} Modul{(p.module_ids || []).length === 1 ? "" : "e"} gebucht</div>
                    )}
                  </div>
                  <div className={`text-right ${quotaColor}`}>
                    <div className="text-xs uppercase tracking-overline">Anwesenheit</div>
                    <div className="font-mono text-sm">{quota !== null ? `${quota}%` : "—"}</div>
                    {s && <div className="text-[10px] text-slate-500">{s.present}✓ / {s.sick}k / {s.excused}e / {s.unexcused}u</div>}
                  </div>
                  {canManage && (
                    <>
                      <button
                        onClick={() => { setEditingParticipant(p); setKundeDialogOpen(true); }}
                        data-testid={`participant-edit-${p.participant_id}`}
                        className="p-2 border border-slate-200 text-slate-700 hover:bg-slate-100"
                        title="Bearbeiten"
                      >
                        <PenLine size={12} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => openContractPdf(p)}
                        data-testid={`participant-contract-pdf-${p.participant_id}`}
                        className="p-2 border border-slate-200 text-[#0a788c] hover:bg-slate-100"
                        title="Vertrag (PDF)"
                      >
                        <FileText size={12} strokeWidth={1.5} />
                      </button>
                      <a
                        data-testid={`participant-pdf-${p.participant_id}`}
                        href={`${API}/weiterbildung/participants/${p.participant_id}/monthly-pdf/${new Date().toISOString().slice(0,7)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="p-2 border border-slate-200 text-slate-700 hover:bg-slate-100"
                        title="Monats-PDF"
                      >
                        <Download size={12} strokeWidth={1.5} />
                      </a>
                      <button onClick={() => removeParticipant(p)} data-testid={`participant-remove-${p.participant_id}`} className="p-2 border border-slate-200 text-red-600 hover:bg-red-50" title="Aus Klasse entfernen (Kunde bleibt erhalten)">
                        <Trash2 size={12} strokeWidth={1.5} />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "anwesenheit" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-light tracking-tighter">Anwesenheit</h2>
            <button onClick={() => setAttOpen(true)} data-testid="add-attendance-btn" className="text-xs bg-[#0a788c] text-white px-4 py-2 hover:bg-[#0a96b4] flex items-center gap-2">
              <Plus size={12} strokeWidth={1.5} /> Anwesenheit erfassen
            </button>
          </div>
          {attendance.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-400 italic text-sm">Noch keine Anwesenheiten dokumentiert.</div>
          ) : (
            <div className="border border-slate-200 divide-y divide-slate-100">
              {attendance.map((a) => {
                const signed = (a.entries || []).filter((e) => e.participant_signature).length;
                const total = (a.entries || []).length;
                return (
                  <div key={a.attendance_id} className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{formatDeShort(a.date)} · {a.time_from || "?"}–{a.time_to || "?"}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {a.lecturer_signature ? "✓ Dozent unterschrieben" : "Dozent noch nicht unterschrieben"} · {signed}/{total} Teilnehmer unterschrieben
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(a.entries || []).map((e) => {
                        const p = participants.find((pp) => pp.participant_id === e.participant_id);
                        return (
                          <div key={e.participant_id} className={`text-xs border-l-2 px-2 py-1 ${STATUS_STYLES[e.status] || "bg-slate-50 border-slate-300"}`}>
                            <span className="font-medium">{p ? `${p.first_name} ${p.last_name}` : e.participant_id}</span>
                            <span className="mx-1">·</span>
                            <span>{STATUS_LABELS[e.status] || e.status}</span>
                            {e.participant_signature && <span className="ml-1">✓</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "klassenbuch" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-light tracking-tighter">Digitales Klassenbuch</h2>
            <button onClick={() => setDiaryOpen(true)} data-testid="add-diary-btn" className="text-xs bg-[#0a788c] text-white px-4 py-2 hover:bg-[#0a96b4] flex items-center gap-2">
              <Plus size={12} strokeWidth={1.5} /> Eintrag anlegen
            </button>
          </div>
          {diary.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-400 italic text-sm">Noch keine Klassenbuch-Einträge.</div>
          ) : (
            <div className="border border-slate-200 divide-y divide-slate-100">
              {diary.map((d) => (
                <div key={d.diary_id} className="p-4">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="font-mono text-sm">{formatDeShort(d.date)} · {d.time_from || "?"}–{d.time_to || "?"}</div>
                    <div className="text-[10px] uppercase text-slate-400">{d.lecturer_name}</div>
                  </div>
                  {d.topics && <div className="text-sm"><span className="font-medium text-slate-500 uppercase text-[10px] mr-2">Themen:</span>{d.topics}</div>}
                  {d.contents && <div className="text-sm mt-1"><span className="font-medium text-slate-500 uppercase text-[10px] mr-2">Inhalte:</span>{d.contents}</div>}
                  {d.incidents && <div className="text-sm mt-1 text-amber-800"><span className="font-medium text-slate-500 uppercase text-[10px] mr-2">Vorkommnisse:</span>{d.incidents}</div>}
                  {d.participant_notes && <div className="text-sm mt-1 text-slate-600"><span className="font-medium text-slate-500 uppercase text-[10px] mr-2">Teilnehmer-Notiz:</span>{d.participant_notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "fruehwarnung" && (
        <div>
          <h2 className="font-heading text-2xl font-light tracking-tighter mb-4">Frühwarnsystem</h2>
          <p className="text-xs text-slate-500 mb-4">Automatische Anwesenheitsanalyse mit Ampel — sortiert nach Dringlichkeit.</p>
          <div className="border border-slate-200 divide-y divide-slate-100">
            {warnings.length === 0 && <div className="p-8 text-center text-slate-400 italic text-sm">Noch keine Anwesenheitsdaten für Analyse.</div>}
            {warnings.map((w) => {
              const bg = w.level === "red" ? "bg-red-50 border-l-red-500" : w.level === "yellow" ? "bg-amber-50 border-l-amber-500" : "bg-emerald-50 border-l-emerald-500";
              return (
                <div key={w.participant_id} className={`p-4 border-l-4 ${bg}`} data-testid={`warning-${w.participant_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{w.first_name} {w.last_name}</div>
                      <div className="text-xs text-slate-600 mt-1 font-mono">
                        Anwesenheitsquote: <b>{w.quota !== null ? `${w.quota}%` : "—"}</b> · Fehltage gesamt: {w.stats.sick + w.stats.excused + w.stats.unexcused} · Unentschuldigt: {w.stats.unexcused} · Letzter Anwesenheitstag: {w.stats.last_present ? formatDeShort(w.stats.last_present) : "—"}
                      </div>
                      {w.stats.recent_absences >= 3 && (
                        <div className="text-xs text-red-800 mt-1">⚠️ {w.stats.recent_absences} Fehltage in den letzten 14 Tagen — bitte Teilnehmer kontaktieren!</div>
                      )}
                    </div>
                    <a data-testid={`warning-pdf-${w.participant_id}`} href={`${API}/weiterbildung/participants/${w.participant_id}/monthly-pdf/${new Date().toISOString().slice(0,7)}`} target="_blank" rel="noopener noreferrer" className="text-xs border border-slate-300 px-3 py-2 hover:bg-white flex items-center gap-1" title="Monats-PDF">
                      <Download size={11} strokeWidth={1.5} /> PDF
                    </a>
                    <button onClick={() => setInterventionTarget(w)} className="text-xs bg-[#0a788c] text-white px-3 py-2 hover:bg-[#0a96b4] flex items-center gap-1">
                      <Phone size={11} strokeWidth={1.5} /> Intervention
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <WeiterbildungKundeDialog
        open={kundeDialogOpen}
        onOpenChange={(o) => { setKundeDialogOpen(o); if (!o) setEditingParticipant(null); }}
        initialData={editingParticipant}
        onSaved={fetchAll}
      />
      <AssignParticipantDialog open={assignOpen} onOpenChange={setAssignOpen} classId={classId} existingIds={participants.map((p) => p.participant_id)} onSaved={fetchAll} />
      <AttendanceDialog open={attOpen} onOpenChange={setAttOpen} cls={cls} participants={participants} onSaved={fetchAll} />
      <DiaryDialog open={diaryOpen} onOpenChange={setDiaryOpen} classId={classId} defaultFrom={cls?.default_time_from} defaultTo={cls?.default_time_to} onSaved={fetchAll} />
      <InterventionDialog target={interventionTarget} onClose={() => setInterventionTarget(null)} onSaved={fetchAll} />
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors ${
        active ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-900"
      }`}
    >
      <Icon size={14} strokeWidth={1.5} /> {children}
    </button>
  );
}

function AssignParticipantDialog({ open, onOpenChange, classId, existingIds, onSaved }) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [assigningId, setAssigningId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/weiterbildung/participants").then((r) => setAll(r.data)).catch(() => setAll([])).finally(() => setLoading(false));
  }, [open]);

  const candidates = all.filter((p) => !existingIds.includes(p.participant_id)).filter((p) => {
    const name = (p.name || `${p.first_name} ${p.last_name}`).toLowerCase();
    return !filter || name.includes(filter.toLowerCase()) || (p.email || "").toLowerCase().includes(filter.toLowerCase());
  });

  const assign = async (p) => {
    setAssigningId(p.participant_id);
    try {
      await api.post(`/weiterbildung/participants/${p.participant_id}/assign`, { class_id: classId });
      toast.success(`${p.name || p.first_name} zugewiesen`);
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setAssigningId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="assign-participant-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">Teilnehmer zuweisen</DialogTitle>
          <div className="text-xs text-slate-500">Wähle einen bereits erfassten Weiterbildungskunden. Neue Kunden werden unter Weiterbildung → „Kunden" erfasst.</div>
        </DialogHeader>
        <div className="px-6 py-4">
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Suche nach Name oder E-Mail…" className="input-tech text-sm w-full" data-testid="assign-participant-search" />
        </div>
        <div className="px-6 pb-6 max-h-[50vh] overflow-y-auto space-y-1.5">
          {loading && <div className="text-xs text-slate-400 italic">Lädt…</div>}
          {!loading && candidates.length === 0 && <div className="text-xs text-slate-400 italic">Keine passenden Kunden gefunden. Lege neue Kunden zentral unter „Kunden" an.</div>}
          {candidates.map((p) => (
            <div key={p.participant_id} className="flex items-center justify-between gap-3 p-2.5 border border-slate-200 text-xs" data-testid={`assign-participant-option-${p.participant_id}`}>
              <div className="min-w-0">
                <div className="text-slate-900 font-medium">{p.name || `${p.first_name} ${p.last_name}`}</div>
                <div className="text-slate-500 font-mono">{p.email}</div>
              </div>
              <button onClick={() => assign(p)} disabled={assigningId === p.participant_id} className="px-3 py-1.5 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 shrink-0" data-testid={`assign-participant-confirm-${p.participant_id}`}>
                {assigningId === p.participant_id ? "…" : "Zuweisen"}
              </button>
            </div>
          ))}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-slate-100">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm border border-slate-300">Schließen</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-l-2 border-slate-200 pl-3">
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function AttendanceDialog({ open, onOpenChange, cls, participants, onSaved }) {
  const [date, setDate] = useState(todayIso());
  const [tFrom, setTFrom] = useState(cls?.default_time_from || "09:00");
  const [tTo, setTTo] = useState(cls?.default_time_to || "13:00");
  const [statuses, setStatuses] = useState({});
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(todayIso());
      setTFrom(cls?.default_time_from || "09:00");
      setTTo(cls?.default_time_to || "13:00");
      const init = {};
      for (const p of participants) init[p.participant_id] = "present";
      setStatuses(init);
      setSignature(null);
    }
  }, [open, cls, participants]);

  const submit = async () => {
    if (!signature) { toast.error("Bitte unterschreiben"); return; }
    setSaving(true);
    try {
      const entries = participants.map((p) => ({ participant_id: p.participant_id, status: statuses[p.participant_id] || "present" }));
      const res = await api.post("/weiterbildung/attendance", {
        class_id: cls.class_id, date, time_from: tFrom, time_to: tTo, entries, lecturer_signature: signature,
      });
      toast.success(`Anwesenheit gespeichert · ${res.data.signature_mails_sent} Signatur-Mails versendet`);
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none border border-slate-200 p-0" data-testid="attendance-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">Anwesenheit erfassen</DialogTitle>
        </DialogHeader>
        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-1">Datum</label>
              <GermanDateInput className="input-tech" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-1">Von</label>
              <input type="time" className="input-tech" value={tFrom} onChange={(e) => setTFrom(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-1">Bis</label>
              <input type="time" className="input-tech" value={tTo} onChange={(e) => setTTo(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Teilnehmer</label>
            <div className="border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {participants.length === 0 && <div className="text-xs text-slate-400 italic p-3">Keine Teilnehmer in dieser Klasse.</div>}
              {participants.map((p) => (
                <div key={p.participant_id} className="p-3 flex items-center gap-3">
                  <div className="flex-1 text-sm">{p.first_name} {p.last_name}</div>
                  <select className="input-tech text-xs py-1 max-w-[140px]" value={statuses[p.participant_id] || "present"} onChange={(e) => setStatuses((s) => ({ ...s, [p.participant_id]: e.target.value }))}>
                    <option value="present">Anwesend</option>
                    <option value="sick">Krank</option>
                    <option value="excused">Entschuldigt</option>
                    <option value="unexcused">Unentschuldigt</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Dozenten-Unterschrift *</label>
            <SignaturePad value={signature} onChange={setSignature} />
            <p className="text-[11px] text-slate-500 mt-2">Nach dem Speichern bekommt jeder anwesende/entschuldigte/kranke Teilnehmer automatisch eine E-Mail mit Signatur-Link.</p>
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-slate-100">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm border border-slate-300">Abbrechen</button>
          <button onClick={submit} disabled={saving} data-testid="attendance-save-btn" className="px-4 py-2 text-sm bg-slate-900 text-white disabled:opacity-50">{saving ? "Speichere…" : "Anwesenheit sichern"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiaryDialog({ open, onOpenChange, classId, defaultFrom, defaultTo, onSaved }) {
  const [form, setForm] = useState({ date: todayIso(), time_from: "", time_to: "", topics: "", contents: "", incidents: "", participant_notes: "" });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [hints, setHints] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [weekInfo, setWeekInfo] = useState(null); // { week: {...}, has_plan: bool }
  useEffect(() => {
    if (open) {
      setForm({ date: todayIso(), time_from: defaultFrom || "09:00", time_to: defaultTo || "13:00", topics: "", contents: "", incidents: "", participant_notes: "" });
      setHints(""); setReasoning(""); setWeekInfo(null);
    }
  }, [open, defaultFrom, defaultTo]);

  // Fetch week-info whenever date changes
  useEffect(() => {
    if (!open || !form.date) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/weiterbildung/classes/${classId}/week-for-date/${form.date}`);
        if (!cancelled) setWeekInfo(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, form.date, classId]);

  const addTopicFromChip = (label) => {
    setForm((f) => {
      const existing = (f.topics || "").trim();
      if (existing && existing.split(/[,·\n]/).map(s => s.trim()).includes(label.trim())) return f;
      return { ...f, topics: existing ? `${existing}, ${label}` : label };
    });
  };

  const suggest = async () => {
    if (!form.date) { toast.error("Bitte Datum wählen"); return; }
    setSuggesting(true); setReasoning("");
    try {
      const { data } = await api.post(`/weiterbildung/classes/${classId}/diary-suggest`, { date: form.date, hints: hints || null });
      setForm((f) => ({
        ...f,
        topics: data.topics || f.topics,
        contents: data.contents || f.contents,
      }));
      setReasoning(data.reasoning || "");
      toast.success(`KI-Vorschlag eingefügt${data.suggested_topic_block ? ` (${data.suggested_topic_block})` : ""}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async () => {
    if (!form.topics && !form.contents) { toast.error("Bitte Themen oder Inhalte angeben"); return; }
    setSaving(true);
    try {
      await api.post("/weiterbildung/diary", { class_id: classId, ...form });
      toast.success("Klassenbuch-Eintrag gespeichert");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-none border border-slate-200 p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">Klassenbuch-Eintrag</DialogTitle>
        </DialogHeader>
        <div className="px-6 py-5 space-y-3 max-h-[65vh] overflow-y-auto">
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs uppercase text-slate-500 block mb-1">Datum</label><GermanDateInput className="input-tech" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></div>
            <div><label className="text-xs uppercase text-slate-500 block mb-1">Von</label><input type="time" className="input-tech" value={form.time_from} onChange={(e) => setForm({...form, time_from: e.target.value})} /></div>
            <div><label className="text-xs uppercase text-slate-500 block mb-1">Bis</label><input type="time" className="input-tech" value={form.time_to} onChange={(e) => setForm({...form, time_to: e.target.value})} /></div>
          </div>
          {/* --- KI-Vorschlag Panel --- */}
          <div className="border border-[#0a788c]/30 bg-[#e8f6f9]/40 p-3">
            <div className="flex items-center gap-2 mb-2">
              <PenLine size={14} strokeWidth={1.5} className="text-[#0a788c]" />
              <div className="text-xs font-bold uppercase tracking-overline text-[#0a788c]">KI-Vorschlag aus Klassen-Konzept</div>
            </div>
            <input
              className="input-tech text-xs mb-2"
              placeholder="Optional: Stichworte (z. B. 'Midjourney Grundkurs, Bildkomposition')"
              value={hints}
              onChange={(e) => setHints(e.target.value)}
              data-testid="diary-suggest-hints"
            />
            <button
              type="button"
              onClick={suggest}
              disabled={suggesting}
              className="px-3 py-2 bg-[#0a788c] text-white text-xs font-medium hover:bg-[#0a96b4] disabled:opacity-50 flex items-center gap-2"
              data-testid="diary-suggest-btn"
            >
              {suggesting ? "KI schreibt…" : "Themen + Inhalte vorschlagen"}
            </button>
            {reasoning && (
              <div className="text-[10px] text-slate-600 italic mt-2 border-l-2 border-[#0a788c] pl-2">
                {reasoning}
              </div>
            )}
          </div>
          <div><label className="text-xs uppercase text-slate-500 block mb-1">Behandelte Themen</label>
          {weekInfo?.week && (
            <div className="mb-2 border border-[#0a788c]/20 bg-[#e8f6f9]/50 p-2" data-testid="week-chips">
              <div className="text-[10px] font-bold uppercase tracking-overline text-[#0a788c] mb-1">
                Woche {weekInfo.week.week_number}: {weekInfo.week.theme || ""}
              </div>
              <div className="flex flex-wrap gap-1">
                {(weekInfo.week.topics || []).concat(weekInfo.week.subtopics || []).map((t, i) => (
                  <button
                    key={`${t}-${i}`}
                    type="button"
                    onClick={() => addTopicFromChip(t)}
                    className="text-[11px] px-2 py-1 bg-white border border-slate-300 hover:bg-[#0a788c] hover:text-white hover:border-[#0a788c] transition-colors"
                    data-testid={`week-chip-${i}`}
                    title="Klicken um zu 'Themen' hinzuzufügen"
                  >
                    + {t}
                  </button>
                ))}
              </div>
            </div>
          )}
          {weekInfo && !weekInfo.week && weekInfo.has_plan && (
            <div className="mb-2 text-[10px] text-slate-500 italic">Für dieses Datum ist keine Woche im Plan hinterlegt.</div>
          )}
          <textarea rows={2} className="input-tech" value={form.topics} onChange={(e) => setForm({...form, topics: e.target.value})} placeholder="z.B. OSI-Modell, TCP/IP" data-testid="diary-topics" /></div>
          <div><label className="text-xs uppercase text-slate-500 block mb-1">Unterrichtsinhalte</label><textarea rows={4} className="input-tech" value={form.contents} onChange={(e) => setForm({...form, contents: e.target.value})} placeholder="Ausführliche Beschreibung des Unterrichts" data-testid="diary-contents" /></div>
          <div><label className="text-xs uppercase text-slate-500 block mb-1">Besondere Vorkommnisse</label><textarea rows={2} className="input-tech" value={form.incidents} onChange={(e) => setForm({...form, incidents: e.target.value})} placeholder="Optional" /></div>
          <div><label className="text-xs uppercase text-slate-500 block mb-1">Teilnehmer-Hinweise</label><textarea rows={2} className="input-tech" value={form.participant_notes} onChange={(e) => setForm({...form, participant_notes: e.target.value})} placeholder="Optional — Notizen zu einzelnen Teilnehmern" /></div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-slate-100">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm border border-slate-300">Abbrechen</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm bg-slate-900 text-white disabled:opacity-50">{saving ? "Speichere…" : "Eintrag sichern"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const INTERV_KINDS = [
  { v: "contacted", l: "Teilnehmer kontaktiert" },
  { v: "phone", l: "Telefonat geführt" },
  { v: "email", l: "E-Mail versendet" },
  { v: "meeting", l: "Beratungsgespräch" },
  { v: "clarified", l: "Grund für Fehlzeiten geklärt" },
  { v: "jobcenter", l: "Jobcenter/Arbeitsagentur informiert" },
  { v: "next_appointment", l: "Neuer Termin vereinbart" },
  { v: "other", l: "Sonstiges" },
];

function InterventionDialog({ target, onClose, onSaved }) {
  const [kind, setKind] = useState("phone");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  useEffect(() => {
    if (target) {
      setKind("phone"); setNotes("");
      api.get(`/weiterbildung/participants/${target.participant_id}/interventions`).then(({data}) => setHistory(data)).catch(() => setHistory([]));
    }
  }, [target]);
  if (!target) return null;
  const submit = async () => {
    if (!notes.trim()) { toast.error("Bitte kurze Notiz eingeben"); return; }
    setSaving(true);
    try {
      await api.post("/weiterbildung/interventions", { participant_id: target.participant_id, class_id: target.class_id, kind, notes });
      toast.success("Intervention dokumentiert");
      setNotes("");
      const { data } = await api.get(`/weiterbildung/participants/${target.participant_id}/interventions`);
      setHistory(data);
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl rounded-none border border-slate-200 p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <div className="text-xs uppercase text-slate-500">Intervention · {target.first_name} {target.last_name}</div>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">Intervention dokumentieren</DialogTitle>
        </DialogHeader>
        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
          <div><label className="text-xs uppercase text-slate-500 block mb-1">Art der Intervention</label>
            <select className="input-tech" value={kind} onChange={(e) => setKind(e.target.value)}>
              {INTERV_KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
            </select>
          </div>
          <div><label className="text-xs uppercase text-slate-500 block mb-1">Notizen</label><textarea rows={3} className="input-tech" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Was wurde besprochen / veranlasst?" /></div>
          <button onClick={submit} disabled={saving} className="w-full bg-[#0a788c] text-white py-3 text-sm hover:bg-[#0a96b4] disabled:opacity-50">{saving ? "Speichere…" : "Intervention speichern"}</button>
          {history.length > 0 && (
            <div>
              <div className="text-xs uppercase text-slate-500 mb-2 mt-4">Bisherige Interventionen</div>
              <div className="border border-slate-200 divide-y divide-slate-100">
                {history.map((h) => (
                  <div key={h.intervention_id} className="p-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{INTERV_KINDS.find((k) => k.v === h.kind)?.l || h.kind}</span>
                      <span className="text-[10px] text-slate-400 font-mono">{new Date(h.created_at).toLocaleString("de-DE")} · {h.by_name}</span>
                    </div>
                    <div className="text-xs text-slate-700">{h.notes}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300">Schließen</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -------------------- Monthly Matrix (Klassen-Anwesenheitsliste) --------------------
const STATUS_CYCLE = ["present", "sick", "excused", "unexcused", ""];
const STATUS_SHORT = { present: "✓", sick: "K", excused: "E", unexcused: "U", "": "—" };
const STATUS_CELL_CLS = {
  present: "bg-emerald-100 text-emerald-800",
  sick: "bg-blue-100 text-blue-800",
  excused: "bg-amber-100 text-amber-800",
  unexcused: "bg-red-100 text-red-800",
  "": "bg-white text-slate-300",
};
const DE_MONTHS = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const DE_WEEKDAYS = ["So","Mo","Di","Mi","Do","Fr","Sa"];

function MonthlyMatrix({ classId, onChanged, defaultTimeFrom, defaultTimeTo }) {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});     // { "YYYY-MM-DD_participantId": "present" }
  const [signOpen, setSignOpen] = useState(null);  // date to sign
  const [saving, setSaving] = useState(false);

  const fetchMatrix = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/weiterbildung/classes/${classId}/monthly-matrix/${m}`);
      // On first load, if class-period doesn't include "today", default to class start month.
      setData(data); setEdits({});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  // Initial load: pick current month if within class period, else use class start month
  useEffect(() => {
    (async () => {
      try {
        const clsRes = await api.get(`/weiterbildung/classes/${classId}`);
        const cls = clsRes.data;
        const now = new Date();
        const nowIso = now.toISOString().slice(0,7);
        const nowDate = now.toISOString().slice(0,10);
        let m = nowIso;
        if (cls.start_date && nowDate < cls.start_date) m = cls.start_date.slice(0,7);
        else if (cls.end_date && nowDate > cls.end_date) m = cls.end_date.slice(0,7);
        setMonth(m);
        await fetchMatrix(m);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); setLoading(false); }
    })();
  }, [classId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (month) fetchMatrix(month); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const getStatus = (date, pid) => {
    const key = `${date}_${pid}`;
    if (key in edits) return edits[key];
    const att = data?.attendance_by_date?.[date];
    if (!att) return "";
    const entry = (att.entries || []).find((e) => e.participant_id === pid);
    return entry?.status || "";
  };

  const cycleStatus = (date, pid) => {
    const cur = getStatus(date, pid);
    const idx = STATUS_CYCLE.indexOf(cur);
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
    setEdits((m) => ({ ...m, [`${date}_${pid}`]: next }));
  };

  const dayHasEdits = (date) => Object.keys(edits).some((k) => k.startsWith(`${date}_`));
  const dayIsSigned = (date) => !!(data?.attendance_by_date?.[date]?.lecturer_signature);

  const saveAndSignDay = async (date, signature) => {
    if (!data) return;
    setSaving(true);
    try {
      const entries = (data.participants || []).map((p) => {
        const s = getStatus(date, p.participant_id) || "present";
        return { participant_id: p.participant_id, status: s };
      });
      const res = await api.post("/weiterbildung/attendance", {
        class_id: classId, date,
        time_from: data?.class?.default_time_from || defaultTimeFrom,
        time_to: data?.class?.default_time_to || defaultTimeTo,
        entries, lecturer_signature: signature,
      });
      toast.success(`Tag ${date} unterschrieben · ${res.data.signature_mails_sent} Signatur-Mails versendet`);
      setSignOpen(null);
      await fetchMatrix(month);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  if (loading || !data || !month) return <div className="text-xs text-slate-500">Lädt Monatsmatrix…</div>;

  const [yr, mo] = month.split("-");
  const monthLabel = `${DE_MONTHS[parseInt(mo,10)-1]} ${yr}`;

  const prevMonth = () => {
    const d = new Date(parseInt(yr,10), parseInt(mo,10)-2, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);
  };
  const nextMonth = () => {
    const d = new Date(parseInt(yr,10), parseInt(mo,10), 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);
  };

  return (
    <div data-testid="monthly-matrix">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="px-3 py-1.5 border border-slate-300 text-xs hover:bg-slate-100">‹</button>
          <h2 className="font-heading text-xl font-light tracking-tighter min-w-[180px] text-center">{monthLabel}</h2>
          <button onClick={nextMonth} className="px-3 py-1.5 border border-slate-300 text-xs hover:bg-slate-100">›</button>
          <a
            href={`${API}/weiterbildung/classes/${classId}/monthly-pdf/${month}`}
            target="_blank" rel="noopener noreferrer"
            className="ml-2 px-3 py-1.5 bg-[#0a788c] text-white text-xs font-medium hover:bg-[#0a96b4] flex items-center gap-1"
            data-testid="class-monthly-pdf-btn"
            title="Sammel-Anwesenheitsliste als PDF (alle Teilnehmer, ganzer Monat)"
          >
            <Download size={12} strokeWidth={1.5} /> Klassen-PDF
          </a>
          <a
            href={`${API}/weiterbildung/classes/${classId}/progress.pdf`}
            target="_blank" rel="noopener noreferrer"
            className="ml-1 px-3 py-1.5 bg-[#00CCC9] text-[#0A0A0A] text-xs font-semibold hover:brightness-105 flex items-center gap-1"
            data-testid="class-progress-pdf-btn"
            title="Wochenplan mit Fortschritt & Anwesenheitsstatistik pro Woche (für Träger)"
          >
            <Download size={12} strokeWidth={1.5} /> Fortschritts-PDF
          </a>
        </div>
        <div className="text-xs text-slate-500">
          {data.school_days.length} Unterrichtstage · Wochenenden und Berliner Feiertage ausgeschlossen · Klick eine Zelle um Status zu wechseln
        </div>
      </div>

      {data.participants.length === 0 ? (
        <div className="border border-dashed p-16 text-center text-slate-400 italic">Keine Teilnehmer in dieser Klasse.</div>
      ) : data.school_days.length === 0 ? (
        <div className="border border-dashed p-16 text-center text-slate-400 italic">Keine Unterrichtstage in diesem Monat innerhalb des Weiterbildungszeitraums.</div>
      ) : (
        <div className="overflow-x-auto border border-slate-200">
          <table className="text-xs w-full" data-testid="matrix-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-2 py-2 text-left font-bold uppercase text-[10px] tracking-overline text-slate-500 sticky left-0 bg-slate-50 z-10 min-w-[180px]">Teilnehmer</th>
                {data.school_days.map((d) => {
                  const dd = new Date(d + "T00:00:00");
                  const signed = dayIsSigned(d);
                  const dirty = dayHasEdits(d);
                  return (
                    <th key={d} className={`px-1 py-2 text-center font-mono min-w-[36px] ${signed ? "bg-emerald-50" : dirty ? "bg-amber-50" : ""}`}>
                      <div className="text-[9px] text-slate-500">{DE_WEEKDAYS[dd.getDay()]}</div>
                      <div className="text-sm font-bold">{d.slice(8,10)}</div>
                      {signed && <div className="text-emerald-600 text-[9px]">✓ signiert</div>}
                    </th>
                  );
                })}
                <th className="px-2 py-2 text-center min-w-[80px]">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {data.participants.map((p) => (
                <tr key={p.participant_id} className="border-b border-slate-100">
                  <td className="px-2 py-1 sticky left-0 bg-white z-10 border-r border-slate-100">
                    <div className="font-medium">{p.last_name}, {p.first_name}</div>
                    <div className="text-[10px] text-slate-400 font-mono truncate max-w-[160px]">{p.email}</div>
                  </td>
                  {data.school_days.map((d) => {
                    const s = getStatus(d, p.participant_id);
                    return (
                      <td
                        key={d}
                        onClick={() => cycleStatus(d, p.participant_id)}
                        className={`px-1 py-2 text-center cursor-pointer border-r border-slate-100 hover:opacity-70 ${STATUS_CELL_CLS[s]}`}
                        title={`${d}: ${STATUS_LABELS[s] || "leer"} (klicken zum Wechseln)`}
                      >
                        <span className="font-bold text-[13px]">{STATUS_SHORT[s]}</span>
                      </td>
                    );
                  })}
                  <td className="px-2 py-1">
                    <a
                      data-testid={`matrix-pdf-${p.participant_id}`}
                      href={`${API}/weiterbildung/participants/${p.participant_id}/monthly-pdf/${month}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-[10px] border border-slate-300 px-2 py-1 hover:bg-slate-100 flex items-center gap-1 w-fit"
                      title="Monats-PDF"
                    >
                      <Download size={10} strokeWidth={1.5} /> PDF
                    </a>
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-50">
                <td className="px-2 py-2 sticky left-0 bg-slate-50 font-bold text-[10px] uppercase tracking-overline text-slate-500 border-r border-slate-100">Tag abschließen</td>
                {data.school_days.map((d) => (
                  <td key={d} className="px-1 py-2 text-center">
                    <button
                      onClick={() => setSignOpen(d)}
                      disabled={saving}
                      className={`text-[10px] px-2 py-1 ${dayIsSigned(d) ? "bg-emerald-100 text-emerald-800" : "bg-[#0a788c] text-white hover:bg-[#0a96b4]"} disabled:opacity-50`}
                      title={dayIsSigned(d) ? "Bereits signiert — Erneut signieren (überschreiben)" : "Tag digital signieren + Mails versenden"}
                    >
                      {dayIsSigned(d) ? "✓" : "Signieren"}
                    </button>
                  </td>
                ))}
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-3 mt-4 text-[11px] text-slate-500 flex-wrap">
        <div className="flex items-center gap-1"><span className="inline-block w-4 h-4 bg-emerald-100"></span> Anwesend (✓)</div>
        <div className="flex items-center gap-1"><span className="inline-block w-4 h-4 bg-blue-100"></span> Krank (K)</div>
        <div className="flex items-center gap-1"><span className="inline-block w-4 h-4 bg-amber-100"></span> Entschuldigt (E)</div>
        <div className="flex items-center gap-1"><span className="inline-block w-4 h-4 bg-red-100"></span> Unentschuldigt (U)</div>
      </div>

      <SignDayDialog date={signOpen} onCancel={() => setSignOpen(null)} onSign={saveAndSignDay} saving={saving} />
    </div>
  );
}

function ClassOverview({ dashboard, onGotoTab, classId, classData }) {
  if (!dashboard) return <div className="text-xs text-slate-500">Lädt Übersicht…</div>;
  const {
    kpis,
    ampel_distribution: ampel,
    open_signatures,
    open_signatures_total_days: openSigTotalDays = 0,
    open_signatures_total_entries: openSigTotalEntries = 0,
    unsigned_days,
    unsigned_days_total: unsignedTotal = 0,
    recent_diary,
  } = dashboard;
  const totalAmpel = (ampel.green || 0) + (ampel.yellow || 0) + (ampel.red || 0);
  const pct = (v) => (totalAmpel ? Math.round((v || 0) / totalAmpel * 100) : 0);
  const SIG_LIMIT = 6;
  const shownSigs = (open_signatures || []).slice(0, SIG_LIMIT);
  const moreSigs = Math.max(openSigTotalDays - shownSigs.length, 0);
  const shownUnsigned = (unsigned_days || []).slice(0, SIG_LIMIT);
  const moreUnsigned = Math.max(unsignedTotal - shownUnsigned.length, 0);
  return (
    <div data-testid="class-overview" className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Teilnehmer" value={kpis.participant_count} />
        <Kpi
          label="Ø Anwesenheitsquote"
          value={kpis.avg_attendance_quota !== null ? `${kpis.avg_attendance_quota}%` : "—"}
          tint={
            kpis.avg_attendance_quota !== null && kpis.avg_attendance_quota < 75
              ? "red"
              : kpis.avg_attendance_quota !== null && kpis.avg_attendance_quota < 90
              ? "amber"
              : "emerald"
          }
        />
        <Kpi
          label={kpis.total_weeks ? "Wochen-Fortschritt" : "Kurs-Fortschritt"}
          value={kpis.total_weeks
            ? (kpis.current_week ? `Woche ${kpis.current_week}/${kpis.total_weeks}` : "—")
            : (kpis.class_progress_pct !== null ? `${kpis.class_progress_pct}%` : "—")}
          sub={kpis.total_weeks
            ? (kpis.current_week ? `${kpis.total_weeks - kpis.current_week} Wochen verbleibend` : "Kurs noch nicht gestartet")
            : `${kpis.elapsed_school_days || 0}/${kpis.total_school_days || 0} Unterrichtstage · ${kpis.recorded_days} erfasst`}
        />
        <Kpi label="Fehltage gesamt" value={kpis.total_absences} />
      </div>

      <div className="border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Ampel-Verteilung</div>
            <div className="font-heading text-2xl font-light tracking-tighter mt-1">Frühwarnstatus</div>
          </div>
          <button
            onClick={() => onGotoTab("fruehwarnung")}
            className="text-xs border border-slate-900 px-3 py-2 hover:bg-slate-900 hover:text-white flex items-center gap-1"
            data-testid="goto-fruehwarnung-btn"
          >
            Details <ChevronRight size={12} strokeWidth={1.5} />
          </button>
        </div>
        <div className="h-4 flex overflow-hidden border border-slate-200">
          {ampel.green > 0 && <div className="bg-emerald-400" style={{ width: `${pct(ampel.green)}%` }} title={`${ampel.green} grün`} />}
          {ampel.yellow > 0 && <div className="bg-amber-400" style={{ width: `${pct(ampel.yellow)}%` }} title={`${ampel.yellow} gelb`} />}
          {ampel.red > 0 && <div className="bg-red-500" style={{ width: `${pct(ampel.red)}%` }} title={`${ampel.red} rot`} />}
        </div>
        <div className="grid grid-cols-3 gap-4 mt-3">
          <div className="border-l-2 border-emerald-400 pl-3 py-1">
            <div className="text-2xl font-heading font-light">{ampel.green}</div>
            <div className="text-[10px] uppercase tracking-overline text-slate-500">Grün</div>
          </div>
          <div className="border-l-2 border-amber-400 pl-3 py-1">
            <div className="text-2xl font-heading font-light">{ampel.yellow}</div>
            <div className="text-[10px] uppercase tracking-overline text-slate-500">Gelb</div>
          </div>
          <div className="border-l-2 border-red-500 pl-3 py-1">
            <div className="text-2xl font-heading font-light">{ampel.red}</div>
            <div className="text-[10px] uppercase tracking-overline text-slate-500">Rot</div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Panel
          title={`Offene Teilnehmer-Signaturen${openSigTotalDays ? ` · ${openSigTotalEntries} an ${openSigTotalDays} Tag${openSigTotalDays === 1 ? "" : "en"}` : ""}`}
          icon={PenLine}
          empty={openSigTotalDays === 0 ? "Keine offenen Signaturen." : null}
          action={<button data-testid="goto-anwesenheit-btn" onClick={() => onGotoTab("anwesenheit")} className="text-[10px] uppercase text-slate-500 hover:text-slate-900">Alle →</button>}
        >
          <div className="divide-y divide-slate-100" data-testid="open-signatures-list">
            {shownSigs.map((o) => (
              <div key={o.date} className="py-2 flex items-center justify-between" data-testid={`open-sig-${o.date}`}>
                <div>
                  <div className="text-sm font-medium">{formatDeShort(o.date)}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{o.time_from || "?"}–{o.time_to || "?"} Uhr</div>
                </div>
                <div className="text-xs">
                  <b className="text-red-600">{o.open_count}</b><span className="text-slate-400">/{o.total_count} offen</span>
                </div>
              </div>
            ))}
            {moreSigs > 0 && (
              <button
                onClick={() => onGotoTab("anwesenheit")}
                className="w-full py-3 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-50 font-medium"
                data-testid="more-open-sigs-btn"
              >
                +{moreSigs} weitere anzeigen →
              </button>
            )}
          </div>
        </Panel>

        <Panel
          title={`Tage ohne Dozenten-Signatur${unsignedTotal ? ` · ${unsignedTotal}` : ""}`}
          icon={AlertTriangle}
          empty={unsignedTotal === 0 ? "Alle Tage signiert." : null}
          action={<button data-testid="goto-monatsplan-btn" onClick={() => onGotoTab("monatsplan")} className="text-[10px] uppercase text-slate-500 hover:text-slate-900">Monatsplan →</button>}
        >
          <div className="divide-y divide-slate-100" data-testid="unsigned-days-list">
            {shownUnsigned.map((d) => (
              <div key={d.date} className="py-2 flex items-center justify-between" data-testid={`unsigned-${d.date}`}>
                <div className="text-sm">{formatDeShort(d.date)}</div>
                <div className="text-[10px] text-slate-500 font-mono">{d.time_from || "?"}–{d.time_to || "?"} Uhr</div>
              </div>
            ))}
            {moreUnsigned > 0 && (
              <button
                onClick={() => onGotoTab("monatsplan")}
                className="w-full py-3 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-50 font-medium"
                data-testid="more-unsigned-btn"
              >
                +{moreUnsigned} weitere Tage →
              </button>
            )}
          </div>
        </Panel>
      </div>

      <Panel
        title="Zuletzt im Klassenbuch"
        icon={BookOpen}
        empty={recent_diary.length === 0 ? "Noch keine Einträge." : null}
        action={<button data-testid="goto-klassenbuch-btn" onClick={() => onGotoTab("klassenbuch")} className="text-[10px] uppercase text-slate-500 hover:text-slate-900">Alle →</button>}
      >
        <div className="divide-y divide-slate-100">
          {recent_diary.map((d) => (
            <div key={d.diary_id} className="py-3">
              <div className="flex items-baseline justify-between mb-1">
                <div className="text-sm font-medium">{formatDeShort(d.date)}</div>
                <div className="text-[10px] uppercase text-slate-400">{d.lecturer_name}</div>
              </div>
              {d.topics && <div className="text-xs text-slate-600 line-clamp-2">{d.topics}</div>}
            </div>
          ))}
        </div>
      </Panel>

      {(classData?.google_classroom_url || classData?.classroom_course_id) && (
        <GoogleCourseworkWidget classId={classId} lang="de" />
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tint }) {
  const cls =
    tint === "red" ? "border-l-red-500"
    : tint === "amber" ? "border-l-amber-500"
    : tint === "emerald" ? "border-l-emerald-500"
    : "border-l-slate-900";
  return (
    <div className={`border border-slate-200 border-l-4 ${cls} p-4`}>
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{label}</div>
      <div className="font-heading text-3xl font-light tracking-tighter mt-1">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function WeeklyPlanView({ weeks, classStart, classEnd, classId, canEdit, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [editIdx, setEditIdx] = useState(null);
  const [editSub, setEditSub] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = (w, i) => {
    setEditIdx(i);
    setEditSub((w.subtopics || []).join(", "));
  };
  const saveSub = async (w) => {
    setSaving(true);
    try {
      await api.patch(`/weiterbildung/classes/${classId}/week/${w.week_number}`, {
        subtopics: editSub.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`Woche ${w.week_number} gespeichert`);
      setEditIdx(null);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };
  const toggleTaught = async (w, next) => {
    try {
      await api.patch(`/weiterbildung/classes/${classId}/week/${w.week_number}`, { taught: next });
      toast.success(next ? `Woche ${w.week_number} als unterrichtet markiert` : `Markierung entfernt`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!weeks || weeks.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500" data-testid="weekly-plan-empty">
        <div className="text-sm mb-2">Kein Wochenplan hinterlegt.</div>
        <div className="text-xs text-slate-400">Admin kann den Plan aus dem Klassen-Konzept via KI generieren (Weiterbildung → Klasse bearbeiten → Konzept → &bdquo;Wochen-Plan generieren&ldquo;).</div>
      </div>
    );
  }
  return (
    <div data-testid="weekly-plan-view">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Curriculum · Wochenplan</div>
          <div className="text-xs text-slate-500 font-mono">
            {classStart && `${classStart}`} – {classEnd} · {weeks.length} Wochen insgesamt
          </div>
        </div>
        {canEdit && (
          <div className="text-[10px] text-slate-500 italic">
            Zertifizierte Wochenthemen sind gesperrt. Unterthemen kannst du anpassen.
          </div>
        )}
      </div>
      <div className="border border-slate-200 divide-y divide-slate-100">
        {weeks.map((w, i) => {
          const isCurrent = w.start_date && w.end_date && w.start_date <= today && today <= w.end_date;
          const isPast = w.end_date && w.end_date < today;
          const isTaught = !!w.taught || (isPast && w.taught !== false);
          return (
            <div
              key={w.week_number}
              className={`p-4 ${isCurrent ? "bg-[#e8f6f9] border-l-4 border-l-[#0a788c]" : ""} ${isPast && !isTaught ? "bg-amber-50/40" : ""}`}
              data-testid={`weekplan-row-${w.week_number}`}
            >
              <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="font-heading text-xl font-light tracking-tighter text-slate-900 min-w-[80px]">
                    Woche {w.week_number}
                  </div>
                  <div className="text-sm font-medium text-slate-900">{w.theme || "—"}</div>
                  {isCurrent && (
                    <span className="text-[10px] font-bold uppercase tracking-overline bg-[#0a788c] text-white px-2 py-0.5" data-testid="current-week-badge">
                      Aktuell
                    </span>
                  )}
                  {isTaught && (
                    <span className="text-[10px] font-bold uppercase tracking-overline bg-emerald-600 text-white px-2 py-0.5 flex items-center gap-1">
                      <CheckCircle2 size={10} strokeWidth={2} /> unterrichtet
                    </span>
                  )}
                  {isPast && !isTaught && (
                    <span className="text-[10px] font-bold uppercase tracking-overline bg-amber-500 text-white px-2 py-0.5">offen</span>
                  )}
                </div>
                <div className="text-[10px] font-mono text-slate-500">{w.start_date} – {w.end_date}</div>
              </div>
              {(w.topics || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {(w.topics || []).map((t, ti) => (
                    <span key={ti} className="text-[11px] px-2 py-1 bg-slate-900 text-white" title="Zertifiziertes Curriculum-Thema (nicht editierbar)">{t}</span>
                  ))}
                </div>
              )}
              {editIdx === i ? (
                <div className="mt-2 space-y-2">
                  <input
                    className="input-tech text-xs"
                    value={editSub}
                    onChange={(e) => setEditSub(e.target.value)}
                    placeholder="Unterthemen (kommagetrennt)"
                    data-testid={`week-subtopics-edit-${w.week_number}`}
                  />
                  <div className="flex gap-2">
                    <button onClick={() => saveSub(w)} disabled={saving} className="px-3 py-1.5 bg-slate-900 text-white text-xs disabled:opacity-50">
                      {saving ? "Speichere…" : "Speichern"}
                    </button>
                    <button onClick={() => setEditIdx(null)} className="px-3 py-1.5 border border-slate-300 text-xs">
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {(w.subtopics || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(w.subtopics || []).map((t, ti) => (
                        <span key={ti} className="text-[11px] px-2 py-1 border border-slate-300 text-slate-700 bg-white">{t}</span>
                      ))}
                    </div>
                  )}
                  {canEdit && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <button
                        onClick={() => startEdit(w, i)}
                        className="text-[10px] px-2 py-1 border border-slate-300 hover:bg-slate-50"
                        data-testid={`edit-subtopics-${w.week_number}`}
                      >
                        Unterthemen bearbeiten
                      </button>
                      <button
                        onClick={() => toggleTaught(w, !isTaught)}
                        className={`text-[10px] px-2 py-1 ${isTaught ? "border border-slate-300 hover:bg-slate-50" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                        data-testid={`toggle-taught-${w.week_number}`}
                      >
                        {isTaught ? "Als nicht unterrichtet markieren" : "Als unterrichtet markieren ✓"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function Panel({ title, icon: Icon, action, empty, children }) {
  return (
    <div className="border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={14} strokeWidth={1.5} className="text-slate-500" />
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">{title}</div>
        </div>
        {action}
      </div>
      {empty ? <div className="text-xs text-slate-400 italic py-4">{empty}</div> : children}
    </div>
  );
}


function SignDayDialog({ date, onCancel, onSign, saving }) {
  const [signature, setSignature] = useState(null);
  useEffect(() => { if (date) setSignature(null); }, [date]);
  if (!date) return null;
  return (
    <Dialog open={!!date} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <div className="text-xs uppercase text-slate-500">Tag signieren · {date}</div>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">Dozenten-Unterschrift</DialogTitle>
        </DialogHeader>
        <div className="px-6 py-5 space-y-4">
          <p className="text-xs text-slate-600">Alle Status-Änderungen für den {date} werden gespeichert. Danach bekommt jeder anwesende/kranke/entschuldigte Teilnehmer eine E-Mail mit seinem persönlichen Signatur-Link.</p>
          <SignaturePad value={signature} onChange={setSignature} />
        </div>
        <DialogFooter className="px-6 py-4 border-t border-slate-100">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-slate-300">Abbrechen</button>
          <button
            onClick={() => onSign(date, signature)}
            disabled={saving || !signature}
            className="px-4 py-2 text-sm bg-slate-900 text-white disabled:opacity-50"
          >
            {saving ? "Speichere…" : "Tag signieren + Mails senden"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

